import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Home, ListChecks, UtensilsCrossed, Users, LayoutGrid, LogOut, Sparkles, Monitor, Download, X, Newspaper, CalendarDays, ChevronDown, Check, Plus, QrCode, Copy, MessageCircle, Mail, UserPlus, ArrowLeft, Wallet } from "lucide-react";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { Tip } from "@/components/Tip";
import { ROLE_LABELS } from "@/lib/labels";
import { initials } from "@/components/AiComponents";
import { OrgIconContent } from "@/components/OrgIcon";
import OnboardingSlideshow from "@/components/OnboardingSlideshow";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";

const ROLE_STYLE = { beheerder: "bg-brand-violet text-white", super_admin: "bg-brand-ink text-white", gebruiker: "bg-white" };
const TYPE_EMOJI = { huishouden: "🏠", gezin: "👨‍👩‍👧", studentenhuis: "🎓", vereniging: "🤝", instelling: "🏢", bedrijf: "💼" };
const ORG_COLORS = ["#8B5CFF", "#2DD4BF", "#FBBF24", "#F472B6", "#3B82F6", "#C84BE0", "#F97316", "#22C55E"];

function OrgBadge() {
  const { orgs, activeOrg, switchOrg } = useAuth();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("menu"); // menu | members
  const [members, setMembers] = useState(null);
  const idx = (orgs || []).findIndex((o) => o.org_id === activeOrg);
  const org = idx >= 0 ? orgs[idx] : null;
  const colorOf = (i) => ORG_COLORS[((i % ORG_COLORS.length) + ORG_COLORS.length) % ORG_COLORS.length];
  if (!org) return null;
  const accent = org.theme_accent || colorOf(idx);

  const openPanel = () => { setView("menu"); setOpen(true); };
  const showMembers = async () => {
    setView("members");
    if (members) return;
    try { const { data } = await api.get("/users"); setMembers(data); } catch { setMembers([]); }
  };
  const pick = (id) => { if (id === activeOrg) { setOpen(false); return; } switchOrg(id); window.location.href = "/vandaag"; };

  const link = `${window.location.origin}/aanmelden?org=${activeOrg}`;
  const qr = `${process.env.REACT_APP_BACKEND_URL}/api/app-qr?base_url=${encodeURIComponent(window.location.origin)}&path=${encodeURIComponent(`/aanmelden?org=${activeOrg}`)}`;
  const waText = `Kom erbij in "${org.name}" op Slackr! Meld je hier aan:\n${link}`;

  return (
    <>
      <button data-testid="org-badge" onClick={openPanel}
        className="brutal-sm brutal-press text-white rounded-full pl-1.5 pr-2.5 py-1 flex items-center gap-1.5 max-w-[42vw] sm:max-w-none" style={{ background: accent }}>
        <span className="bg-white/25 rounded-full w-6 h-6 flex items-center justify-center shrink-0 text-sm overflow-hidden"><OrgIconContent org={org} /></span>
        <span className="font-head font-black text-sm truncate">{org.name}</span>
        {orgs.length > 1 && <ChevronDown className="w-4 h-4 shrink-0" strokeWidth={3} />}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)} data-testid="org-panel">
          <div className="bg-brand-cream w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl brutal max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 rounded-t-2xl flex items-start justify-between text-white" style={{ background: accent }}>
              <div className="flex items-center gap-2">
                {view === "members" && <button data-testid="org-back" onClick={() => setView("menu")} className="bg-white/20 hover:bg-white/30 rounded-full p-1"><ArrowLeft className="w-5 h-5" strokeWidth={2.5} /></button>}
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest opacity-80">{view === "members" ? (org.type || "Organisatie") : "Jouw huishoudens"}</p>
                  <h2 className="font-head font-black text-2xl leading-tight">{view === "members" ? org.name : `${orgs.length} ${orgs.length === 1 ? "huishouden" : "huishoudens"}`}</h2>
                  {view === "members" && members && <p className="text-sm font-semibold opacity-90 mt-0.5">{members.length} {members.length === 1 ? "lid" : "leden"} 👋</p>}
                </div>
              </div>
              <button data-testid="org-panel-close" onClick={() => setOpen(false)} className="bg-white/20 hover:bg-white/30 rounded-full p-1.5"><X className="w-5 h-5" strokeWidth={2.5} /></button>
            </div>

            {view === "menu" ? (
              <div className="p-4 space-y-2">
                {orgs.map((o, i) => (
                  <button key={o.org_id} data-testid={`org-switch-${o.org_id}`} onClick={() => pick(o.org_id)}
                    className={`w-full flex items-center gap-3 brutal-sm rounded-xl px-3 py-2.5 text-left ${o.org_id === activeOrg ? "bg-white" : "bg-white/60"}`}>
                    <span className="w-9 h-9 rounded-full flex items-center justify-center text-lg shrink-0 overflow-hidden" style={{ background: (o.theme_accent || colorOf(i)) + "33" }}><OrgIconContent org={o} /></span>
                    <span className="flex-1 min-w-0"><span className="font-head font-black block truncate">{o.name}</span><span className="text-xs font-semibold text-brand-ink/50 capitalize">{o.type} · {ROLE_LABELS[o.role] || o.role}</span></span>
                    {o.org_id === activeOrg && <Check className="w-5 h-5 text-brand-green shrink-0" strokeWidth={3} />}
                  </button>
                ))}
                <button data-testid="org-view-members" onClick={showMembers} className="w-full brutal-sm brutal-press rounded-xl px-3 py-2.5 bg-brand-yellow flex items-center gap-2 font-bold"><Users className="w-5 h-5" strokeWidth={2.5} />Leden bekijken</button>
                <button data-testid="org-add" onClick={() => { window.location.href = "/aanmelden"; }} className="w-full brutal-sm brutal-press rounded-xl px-3 py-2.5 bg-white flex items-center gap-2 font-bold"><Plus className="w-5 h-5" strokeWidth={2.5} />Nieuw huishouden</button>
              </div>
            ) : (
              <div className="p-4 space-y-2">
                {members === null ? (
                  <p className="text-center py-6 font-bold text-brand-ink/50 animate-pulse">Leden laden…</p>
                ) : (
                  <>
                    {members.map((m) => (
                      <div key={m.user_id} className="flex items-center gap-3 brutal-sm rounded-xl bg-white px-3 py-2" data-testid={`org-member-${m.user_id}`}>
                        <div className="w-9 h-9 rounded-full bg-brand-violet text-white flex items-center justify-center font-head font-black overflow-hidden shrink-0">
                          {m.avatar_url ? <img src={m.avatar_url.startsWith("http") ? m.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${m.avatar_url}`} alt="" className="w-full h-full object-cover" /> : initials(m.name)}
                        </div>
                        <span className="flex-1 font-bold truncate">{m.name}</span>
                        <span className={`brutal-sm rounded-full px-2.5 py-0.5 text-xs font-bold ${ROLE_STYLE[m.role] || "bg-white"}`}>{ROLE_LABELS[m.role] || "Lid"}</span>
                      </div>
                    ))}
                    <div className="brutal-sm rounded-xl bg-brand-cream p-3 mt-2" data-testid="org-invite">
                      <p className="font-head font-bold flex items-center gap-2 mb-2"><UserPlus className="w-5 h-5" strokeWidth={2.5} />Lid uitnodigen</p>
                      <div className="flex items-center gap-3">
                        <img src={qr} alt="QR" className="w-20 h-20 rounded-lg bg-white p-1 brutal-sm shrink-0" data-testid="org-invite-qr" />
                        <div className="flex-1 grid grid-cols-3 gap-1.5">
                          <button data-testid="org-invite-copy" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link gekopieerd"); }} className="brutal-sm brutal-press rounded-lg bg-white py-2 flex flex-col items-center gap-0.5 text-[11px] font-bold"><Copy className="w-4 h-4" strokeWidth={2.5} />Link</button>
                          <button data-testid="org-invite-wa" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(waText)}`, "_blank")} className="brutal-sm brutal-press rounded-lg bg-brand-green py-2 flex flex-col items-center gap-0.5 text-[11px] font-bold"><MessageCircle className="w-4 h-4" strokeWidth={2.5} />WhatsApp</button>
                          <button data-testid="org-invite-email" onClick={() => window.open(`mailto:?subject=${encodeURIComponent("Kom bij " + org.name + " op Slackr")}&body=${encodeURIComponent(waText)}`)} className="brutal-sm brutal-press rounded-lg bg-brand-pink py-2 flex flex-col items-center gap-0.5 text-[11px] font-bold"><Mail className="w-4 h-4" strokeWidth={2.5} />E-mail</button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function InstallBanner() {
  const navigate = useNavigate();
  const t = useT();
  const [show, setShow] = useState(false);
  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    const dismissed = localStorage.getItem("slackr_install_banner_dismissed") === "1";
    if (!standalone && !dismissed) setShow(true);
  }, []);
  if (!show) return null;
  const handleInstall = async () => {
    const prompt = window.__doeniesInstallPrompt;
    if (prompt) {
      prompt.prompt();
      try { await prompt.userChoice; } catch (e) {}
      window.__doeniesInstallPrompt = null;
      setShow(false);
    } else {
      navigate("/meer", { state: { openInstall: true } });
    }
  };
  return (
    <div data-testid="install-banner" className="bg-brand-yellow border-b-2 border-brand-ink">
      <div className="max-w-3xl mx-auto px-4 py-2 flex items-center gap-3">
        <Download className="w-5 h-5 shrink-0" strokeWidth={2.5} />
        <p className="text-sm font-bold flex-1 leading-tight">{t("Voeg Slackr toe aan je startscherm voor snelle toegang & meldingen.")}</p>
        <button data-testid="install-banner-cta" onClick={handleInstall}
          className="brutal-sm brutal-press bg-brand-ink text-white rounded-full px-3 py-1.5 text-xs font-bold whitespace-nowrap">{t("Installeren")}</button>
        <button data-testid="install-banner-dismiss" onClick={() => { localStorage.setItem("slackr_install_banner_dismissed", "1"); setShow(false); }} className="p-1"><X className="w-4 h-4" strokeWidth={2.5} /></button>
      </div>
    </div>
  );
}

function InstallButton() {
  const navigate = useNavigate();
  const t = useT();
  const [show, setShow] = useState(false);
  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    setShow(!standalone);
  }, []);
  if (!show) return null;
  const handleInstall = async () => {
    const prompt = window.__doeniesInstallPrompt;
    if (prompt) {
      prompt.prompt();
      try { await prompt.userChoice; } catch (e) {}
      window.__doeniesInstallPrompt = null;
    } else {
      navigate("/meer", { state: { openInstall: true } });
    }
  };
  return (
    <Tip label={t("Installeer de app")}>
      <button data-testid="install-app-btn" onClick={handleInstall}
        className="brutal-sm brutal-press bg-brand-yellow rounded-full p-2 sm:px-3 sm:py-2 font-head font-bold text-sm flex items-center gap-1.5">
        <Download className="w-4 h-4" strokeWidth={2.5} /><span className="hidden sm:inline">{t("Installeer app")}</span>
      </button>
    </Tip>
  );
}

const navBase = [
  { to: "/vandaag", label: "Vandaag", icon: Home, end: true, testid: "nav-vandaag" },
  { to: "/taken", label: "Taken", icon: ListChecks, testid: "nav-taken" },
  { to: "/nieuws", label: "Nieuws", icon: Newspaper, testid: "nav-nieuws", module: "nieuws" },
  { to: "/rooster", label: "Agenda", icon: CalendarDays, testid: "nav-agenda", module: "rooster" },
  { to: "/meer", label: "Meer", icon: LayoutGrid, testid: "nav-meer" },
];

const NAV_I18N = {
  Vandaag: { en: "Today", tr: "Bugün", ar: "اليوم" },
  Taken: { en: "Tasks", tr: "Görevler", ar: "المهام" },
  Nieuws: { en: "News", tr: "Haberler", ar: "الأخبار" },
  Eten: { en: "Food", tr: "Yemek", ar: "الطعام" },
  Agenda: { en: "Agenda", tr: "Ajanda", ar: "الأجندة" },
  Groepen: { en: "Groups", tr: "Gruplar", ar: "المجموعات" },
  Meer: { en: "More", tr: "Daha", ar: "المزيد" },
};
const tNav = (label, lang) => (lang && lang !== "nl" && NAV_I18N[label]?.[lang]) || label;

export default function Layout() {
  const { user, logout, moduleOn, pendingJoins, refresh, zakgeldActive } = useAuth();
  const navigate = useNavigate();
  const nav = navBase.filter((n) => !n.module || moduleOn(n.module));
  if (isAdmin(user) && zakgeldActive) {
    const zg = { to: "/punten?zakgeld=1", label: "Zakgeld", icon: Wallet, testid: "nav-zakgeld" };
    const meerIdx = nav.findIndex((n) => n.label === "Meer");
    if (meerIdx >= 0) nav.splice(meerIdx, 0, zg); else nav.push(zg);
  }
  const kioskSession = typeof window !== "undefined" && localStorage.getItem("doenies_kiosk_session") === "1";
  const lastActive = useRef(Date.now());
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (kioskSession || !user) return;
    const forced = localStorage.getItem("slackr_force_tour") === "1";
    if (!user.onboarded || forced) setTourOpen(true);
    const openTour = () => setTourOpen(true);
    window.addEventListener("open-tour", openTour);
    return () => window.removeEventListener("open-tour", openTour);
  }, [user, kioskSession]);

  const closeTour = async () => {
    setTourOpen(false);
    localStorage.removeItem("slackr_force_tour");
    try { await api.post("/auth/onboarded"); await refresh(); } catch (e) {}
  };

  const exitToBoard = () => {
    localStorage.removeItem("doenies_token");
    localStorage.removeItem("doenies_kiosk_session");
    window.location.href = "/kiosk";
  };

  // Kiosk-sessie: automatisch terug naar het bord na 2 min inactiviteit
  useEffect(() => {
    if (!kioskSession) return;
    const bump = () => { lastActive.current = Date.now(); };
    const evts = ["pointerdown", "touchstart", "keydown", "mousemove"];
    evts.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const iv = setInterval(() => { if (Date.now() - lastActive.current > 120000) exitToBoard(); }, 5000);
    return () => { clearInterval(iv); evts.forEach((e) => window.removeEventListener(e, bump)); };
  }, [kioskSession]);

  return (
    <div className="min-h-screen bg-brand-cream flex flex-col">
      <header className="sticky top-0 z-30 bg-brand-cream border-b-2 border-brand-ink">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="brutal-sm rounded-lg azure-gradient text-white w-9 h-9 flex items-center justify-center">
              <Sparkles className="w-5 h-5" strokeWidth={3} />
            </div>
            <span className="font-head font-black text-2xl tracking-tight">Slackr</span>
          </div>
          <div className="flex items-center gap-3">
            {!kioskSession && <OrgBadge />}
            <span className="hidden sm:block text-sm font-semibold">{user?.name}</span>
            {!kioskSession && <InstallButton />}
            {kioskSession ? (
              <button data-testid="kiosk-back-to-board-btn" onClick={exitToBoard}
                className="brutal-sm brutal-press bg-brand-yellow rounded-full px-4 py-2 font-head font-bold text-sm flex items-center gap-2">
                <Monitor className="w-4 h-4" strokeWidth={2.5} />Terug naar bord
              </button>
            ) : (
              <Tip label="Uitloggen"><button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
                className="brutal-sm brutal-press bg-white rounded-full p-2">
                <LogOut className="w-4 h-4" strokeWidth={2.5} />
              </button></Tip>
            )}
          </div>
        </div>
      </header>

      {!kioskSession && <InstallBanner />}

      {tourOpen && <OnboardingSlideshow isAdmin={isAdmin(user)} onClose={closeTour} />}

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-6 pb-28">
        <Outlet />
      </main>

      <nav className="fixed bottom-0 inset-x-0 z-30 bg-white border-t-2 border-brand-ink">
        <div className="max-w-3xl mx-auto px-2 grid" style={{ gridTemplateColumns: `repeat(${nav.length}, minmax(0, 1fr))` }}>
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 font-head font-bold text-[11px] transition-colors ${isActive ? "text-brand-orange" : "text-brand-ink/50"}`}>
              {({ isActive }) => (
                <>
                  <span className={`relative rounded-xl p-1.5 ${isActive ? "brutal-sm bg-brand-orange text-white" : ""}`}>
                    <n.icon className="w-5 h-5" strokeWidth={2.5} />
                    {n.to === "/meer" && pendingJoins > 0 && (
                      <span data-testid="nav-meer-badge" className="absolute -top-1 -right-1 bg-brand-pink text-white text-[10px] font-black rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center border-2 border-white">{pendingJoins}</span>
                    )}
                  </span>
                  {tNav(n.label, user?.language)}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
